import { useTranslation } from 'react-i18next'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { ProcessorDetails } from '@/lib/api/processors'

interface AuthorizeNetAcceptJSCardProps {
    processor: ProcessorDetails
}

const getClientKey = (options: Record<string, unknown> | null) => {
    return options?.clientKey as string | undefined
}


const getTestMode = (options: Record<string, unknown> | null) => {
    return options?.testMode === true
}

export const AuthorizeNetAcceptJSCard = ({ processor }: AuthorizeNetAcceptJSCardProps) => {
    const { t } = useTranslation('processors')
    const clientKey = getClientKey(processor.options)
    const testMode = getTestMode(processor.options)

    return (
        <Card className="mb-6">
            <CardHeader>
                <CardTitle>Accept.js Integration</CardTitle>
            </CardHeader>
            <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <p className="text-sm text-muted-foreground">Client Key (Public Key)</p>
                        <p className="font-mono text-sm">{clientKey || 'Not configured'}</p>
                    </div>

                    <div>
                        <p className="text-sm text-muted-foreground">Accept.js Status</p>
                        <Badge variant={clientKey ? 'default' : 'secondary'}>
                            {clientKey ? 'Configured' : 'Not Configured'}
                        </Badge>
                    </div>


                    <div className="col-span-full">
                        <div className="rounded-lg bg-blue-50 border border-blue-200 p-4">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div className="ml-3">
                                    <h3 className="text-sm font-medium text-blue-800">
                                        Accept.js Integration
                                    </h3>
                                    <div className="mt-2 text-sm text-blue-700">
                                        <p>
                                            Accept.js allows you to securely collect payment information on your website
                                            without handling sensitive card data directly. Configure your Client Key to enable
                                            tokenized payment processing.
                                        </p>
                                        {!clientKey && (
                                            <p className="mt-2 font-medium">
                                                Add your Client Key in the processor settings to enable Accept.js integration.
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {testMode && (
                        <div className="col-span-full">
                            <div className="rounded-lg bg-amber-50 border border-amber-200 p-4">
                                <div className="flex">
                                    <div className="flex-shrink-0">
                                        <svg className="h-5 w-5 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <div className="ml-3">
                                        <h3 className="text-sm font-medium text-amber-800">
                                            Sandbox Environment
                                        </h3>
                                        <div className="mt-2 text-sm text-amber-700">
                                            <p>
                                                Use Authorize.Net's sandbox Client Key for testing.
                                                Switch to production Client Key when ready to process live transactions.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    )
}
